import React, { useState } from 'react';
import styled from 'styled-components';

const FormContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Title = styled.h2`
  font-size: clamp(36px, 4.5vw, 48px);
  font-weight: 900;
  color: #FFD700;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 4px 20px rgba(255, 215, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: clamp(20px, 2.5vw, 24px);
  color: white;
  opacity: 0.95;
  line-height: 1.6;
  margin-bottom: 15px;
`;

const SubSubtitle = styled.p`
  font-size: clamp(16px, 2vw, 20px);
  color: white;
  opacity: 0.85;
  line-height: 1.5;
  
  span {
    font-size: 0.9em;
    opacity: 0.8;
  }
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 10%;
    right: 10%;
    height: 2px;
    background: #e0e0e0;
    z-index: 0;
  }
`;

const ProgressStep = styled.div<{ active?: boolean; completed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  
  .circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${props => props.completed ? '#4caf50' : props.active ? '#FFD700' : '#e0e0e0'};
    color: ${props => props.active || props.completed ? '#fff' : '#999'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-bottom: 8px;
    transition: all 0.3s;
  }
  
  .label {
    font-size: 12px;
    color: ${props => props.active ? '#333' : '#999'};
    font-weight: ${props => props.active ? '600' : '400'};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  color: #333;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.2s;
  outline: none;
  
  &:focus {
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.2s;
  outline: none;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #FFD700 0%, #ffed4e 100%);
  color: #232B3B;
  padding: 16px 40px;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 6px 20px rgba(255, 215, 0, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 215, 0, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: space-between;
`;

const SecondaryButton = styled(Button)`
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%);
  color: #333;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const Disclaimer = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 20px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 8px;
  line-height: 1.6;
  text-align: center;
`;

const ConfirmationCard = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border: 2px solid #4caf50;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  
  .check-icon {
    font-size: 60px;
    color: #4caf50;
    margin-bottom: 20px;
  }
  
  h3 {
    font-size: 28px;
    color: #2e7d32;
    margin-bottom: 15px;
    font-weight: 800;
  }
  
  p {
    font-size: 18px;
    color: #555;
    line-height: 1.6;
    margin-bottom: 30px;
  }
  
  .highlight {
    background: rgba(255, 215, 0, 0.2);
    padding: 20px;
    border-radius: 10px;
    margin: 20px 0;
    
    h4 {
      color: #333;
      margin-bottom: 10px;
      font-size: 20px;
    }
    
    p {
      font-size: 16px;
      color: #666;
      margin-bottom: 0;
    }
  }
`;

export const QuickAnswerInlineForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    companyName: '',
    productName: '',
    email: '',
    phone: '',
    age: '',
    state: '',
    investmentAmount: '',
    concerns: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Anti-spam fields
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleQuickSubmit = async () => {
    // Anti-spam validation
    if (honeypot) {
      console.warn('Spam detected: honeypot field filled');
      return;
    }

    const timeSpent = Date.now() - formLoadTime;
    if (timeSpent < 3000) {
      console.warn('Spam detected: form submitted too quickly');
      return;
    }

    setIsSubmitting(true);

    try {
      const quickData = {
        from_name: 'Quick Answer User',
        from_email: formData.email,
        insurance_company: formData.companyName || 'Not provided',
        annuity_product: formData.productName || 'Not provided',
        user_message: formData.message || 'Not provided',
        submission_type: 'quick_answer_inline',
        form_source: 'homepage_inline',
        formLoadTime: formLoadTime,
        website: honeypot,
        message: `
QUICK ANSWER REQUEST (Homepage Inline Form)
==========================================

Email: ${formData.email}
Company Name: ${formData.companyName || 'Not provided'}
Product Name: ${formData.productName || 'Not provided'}
Your Message or Questions: ${formData.message || 'Not provided'}

Submitted: ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true })}
        `.trim()
      };
      
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: quickData })
      });
      
      if (response.ok) {
        setCurrentStep(2);
      } else {
        alert('❌ Something went wrong. Please try again.');
      }
    } catch (error) {
      alert('❌ Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  if (currentStep === 2) {
    return (
      <>
        <FormHeader>
          <Title style={{ color: '#FFD700' }}>✨ Excellent! Quick Answer Submitted</Title>
          <Subtitle style={{ color: 'white' }}>
            Our expert will contact you soon
          </Subtitle>
        </FormHeader>
        
        <FormContainer>
          <ConfirmationCard>
            <div className="check-icon">✅</div>
            <h3>Your Quick Answer Request is Being Processed!</h3>
            <p>
              We've received your information{formData.companyName && formData.productName ? ` about ${formData.companyName}'s ${formData.productName}` : ''}.
              <br />One of our annuity experts will reach out to you shortly.
            </p>
            
            <div className="highlight">
              <h4>📊 Want a More Detailed Analysis?</h4>
              <p>
                Get a comprehensive Deep Dive report with historical performance data,
                risk analysis, and personalized recommendations!
              </p>
            </div>
            
            <Button 
              onClick={() => window.location.href = '/deep-dive'}
              style={{ marginTop: '20px', fontSize: '20px', padding: '18px 50px' }}
            >
              Yes, I Want the Deep Dive Analysis!
            </Button>
            
            <p style={{ marginTop: '15px', fontSize: '14px', color: '#999' }}>
              (Takes you to our comprehensive Deep Dive analysis form)
            </p>
          </ConfirmationCard>
        </FormContainer>
      </>
    );
  }

  // Steps 2.5 and 3 removed - now redirects directly to /deep-dive page

  return (
    <>
      <FormHeader>
        <Title>⚡ Quick Answer Now</Title>
      </FormHeader>
      
      <FormContainer>
        <div style={{
          textAlign: 'center',
          marginBottom: '35px',
          paddingBottom: '25px',
          borderBottom: '2px solid #f0f0f0'
        }}>
          <h3 style={{
            fontSize: 'clamp(24px, 3vw, 28px)',
            fontWeight: '700',
            color: '#1a2332',
            marginBottom: '12px'
          }}>
            Get instant feedback on any annuity product!
          </h3>
          <p style={{
            fontSize: 'clamp(16px, 2vw, 18px)',
            color: '#666',
            margin: 0
          }}>
            Fill out this quick form for a quick answer
          </p>
        </div>
        
        <form onSubmit={(e) => { e.preventDefault(); handleQuickSubmit(); }}>
          <FormGroup>
            <Label htmlFor="email">Your Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="companyName">Company Name <span style={{ color: '#10a37f', fontWeight: '600', fontSize: '14px', marginLeft: '8px', background: 'rgba(16, 163, 127, 0.1)', padding: '2px 8px', borderRadius: '4px' }}>(OPTIONAL)</span></Label>
            <Input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="e.g., Allianz, American Equity (optional)"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="productName">Product Name <span style={{ color: '#10a37f', fontWeight: '600', fontSize: '14px', marginLeft: '8px', background: 'rgba(16, 163, 127, 0.1)', padding: '2px 8px', borderRadius: '4px' }}>(OPTIONAL)</span></Label>
            <Input
              type="text"
              id="productName"
              name="productName"
              value={formData.productName}
              onChange={handleChange}
              placeholder="e.g., Core Income 7 (optional)"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="message">Your Message or Questions <span style={{ color: '#10a37f', fontWeight: '600', fontSize: '14px', marginLeft: '8px', background: 'rgba(16, 163, 127, 0.1)', padding: '2px 8px', borderRadius: '4px' }}>(OPTIONAL)</span></Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message || ''}
              onChange={handleChange}
              placeholder="Tell us anything else you'd like us to know or any specific questions you have..."
              style={{ minHeight: '100px' }}
            />
          </FormGroup>

          {/* Honeypot field - hidden from users, catches bots */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <Button type="submit" disabled={isSubmitting} style={{ width: '100%' }}>
            {isSubmitting ? 'Submitting...' : 'Get Quick Answer'}
          </Button>
          
          <Disclaimer>
            🔒 Your information is secure and will never be shared.<br/>
            <strong>One of our experts will contact you soon with full details.</strong>
          </Disclaimer>
        </form>
      </FormContainer>
    </>
  );
};